import { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import GestureIcon from '@mui/icons-material/Gesture';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import { toast } from 'react-toastify';
import useDisclaimer from '../../../../hooks/useDisclaimer';
import WaiverList from '../../../Waivers/WaiverList';

// Waiver (Haftungserklärung) status of a booking. Waivers filled in in advance are
// invisible on the lane-based waiver page, so the counter sees them here: who has
// signed, when, and how many participants are still missing.
function WaiverStatusSection({ bookingId }) {
  const { getDisclaimersForBooking } = useDisclaimer();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!bookingId) return;

    let active = true;
    setIsLoading(true);

    getDisclaimersForBooking(bookingId)
      .then(response => {
        if (active) setData(response);
      })
      .catch(e => {
        // A booking of a branch this user is not assigned to simply has no waiver
        // section here. The backend answers that with its own code since it stopped
        // calling a branch scope a missing right, so it no longer lands in the
        // approval queue — without this it would surface as a load error instead.
        if (e?.code === 'DISCLAIMER_BRANCH_NOT_ACCESSIBLE') return;
        console.error(e);
        toast.error('Fehler beim Laden der Haftungserklärungen');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [bookingId]);

  if (isLoading && !data) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CircularProgress size={18} />
        <Typography variant='body2'>Haftungserklärungen werden geladen…</Typography>
      </Box>
    );
  }

  // Nothing configured and nothing signed: the booking has no waiver process.
  if (!data || (!data.waiverConfigured && !data.waivers?.length)) return null;

  const waivers = Array.isArray(data.waivers) ? data.waivers : [];
  const signedPersonCount = data.signedPersonCount || 0;
  const participantCount = data.participantCount || 0;
  const missingCount = Math.max(0, participantCount - signedPersonCount);
  const advanceCount = waivers.filter(waiver => waiver.source === 'online').length;

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap'>
        <Typography variant='body2' sx={{ fontWeight: 600, mr: 1 }}>
          Haftungserklärung
        </Typography>
        {participantCount > 0 ? (
          <Chip
            color={missingCount === 0 ? 'success' : signedPersonCount > 0 ? 'warning' : 'default'}
            icon={missingCount === 0 ? <DoneIcon /> : <PersonIcon />}
            label={`${signedPersonCount} von ${participantCount} unterschrieben`}
            size='small'
          />
        ) : (
          <Chip
            icon={<GestureIcon />}
            label={`${signedPersonCount} unterschrieben`}
            size='small'
            variant='outlined'
          />
        )}
        {advanceCount > 0 && (
          <Chip
            color='success'
            variant='outlined'
            label={`${advanceCount} vorab ausgefüllt`}
            size='small'
          />
        )}
        {waivers.length === 0 && (
          <Typography variant='body2' color='text.secondary'>
            Noch keine Haftungserklärung eingegangen
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Stack direction='row' spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
          {data.waiverUrl && (
            <Button
              variant='outlined'
              size='small'
              startIcon={<OpenInNewIcon />}
              href={data.waiverUrl}
              target='_blank'
              rel='noreferrer'
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              Formular
            </Button>
          )}
          {waivers.length > 0 && (
            <Button
              variant='contained'
              size='small'
              onClick={() => setExpanded(prev => !prev)}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              {expanded ? 'Ausblenden' : 'Anzeigen'}
            </Button>
          )}
        </Stack>
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ mt: 1 }}>
          <WaiverList waivers={waivers} dense />
        </Box>
      </Collapse>
    </Box>
  );
}

export default WaiverStatusSection;
